/**
 * Clean report printing shared by the report pages.
 *
 * Prints all rows as a plain table from a hidden iframe: no app sidebar/header, no
 * pagination, and a zero page margin so the browser does not print its own URL / date
 * header and footer on the paper.
 *
 * Wide reports (e.g. Employee Salary Register, ~50 columns) cannot fit one sheet, so their
 * columns are printed in parts; each part repeats the key columns (e.g. Serial No, Employee
 * Code, Employee Name) so every row stays identifiable.
 */

const DEFAULT_MAX_COLUMNS = 10;

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Split columns into printable parts; key columns lead every part. */
export function splitPrintColumns(columns, { keyColumns = [], maxColumnsPerTable = DEFAULT_MAX_COLUMNS } = {}) {
  if (columns.length <= maxColumnsPerTable) return [columns];
  const keys = keyColumns.filter((c) => columns.includes(c));
  const others = columns.filter((c) => !keys.includes(c));
  const size = Math.max(4, maxColumnsPerTable - keys.length);
  const parts = [];
  for (let i = 0; i < others.length; i += size) {
    parts.push([...keys, ...others.slice(i, i + size)]);
  }
  return parts;
}

function tableHtml(columns, rows) {
  const head = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join("");
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(r[c] ?? "—")}</td>`).join("")}</tr>`)
    .join("");
  return `<table class="grid"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/** Full print document (exported for tests). */
export function buildPrintHtml({ title, subtitle, columns, rows, keyColumns = [], maxColumnsPerTable }) {
  const parts = splitPrintColumns(columns, { keyColumns, maxColumnsPerTable });
  const widest = Math.max(...parts.map((p) => p.length));
  const landscape = widest > 6;
  const fontSize = widest > 8 ? 11 : 12;
  const sections = parts
    .map((partCols, i) => {
      const caption =
        parts.length > 1
          ? `<p class="part">Part ${i + 1} of ${parts.length}</p>`
          : "";
      return `<section class="section">${caption}${tableHtml(partCols, rows)}</section>`;
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  /* Zero page margin stops the browser printing its own date / URL header and footer;
     spacing is provided by the page layout below instead. */
  @page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; padding: 0 12mm;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Outer layout table: its header/footer rows repeat on every printed page,
     giving each page the same top and bottom margin. */
  .page { width: 100%; border-collapse: collapse; }
  .page > thead td { height: 12mm; padding: 0; }
  .page > tfoot td { height: 12mm; padding: 0; }
  .page > tbody > tr > td { padding: 0; }
  h1 { font-size: 20px; margin: 0 0 6px; }
  .sub { font-size: 12px; color: #475569; margin: 0 0 14px; line-height: 1.5; }
  .section + .section { margin-top: 16px; }
  .part { font-size: 11px; font-weight: 700; color: #1e40af; margin: 0 0 4px; }
  .grid { width: 100%; border-collapse: collapse; font-size: ${fontSize}px; }
  /* Wrap only between words (never one letter per line). */
  .grid th, .grid td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; vertical-align: top;
                       word-break: normal; overflow-wrap: break-word; }
  .grid th { background: #eef2ff; font-weight: 700; }
  .grid tr { page-break-inside: avoid; break-inside: avoid; }
  .grid thead { display: table-header-group; }
</style></head><body>
<table class="page">
  <thead><tr><td></td></tr></thead>
  <tfoot><tr><td></td></tr></tfoot>
  <tbody><tr><td>
    <h1>${escapeHtml(title)}</h1>
    <p class="sub">${escapeHtml(subtitle)}</p>
    ${sections}
  </td></tr></tbody>
</table>
</body></html>`;
}

/** Print the full report (all rows, no app chrome) through a hidden iframe. */
export function printRowsAsTable(options) {
  const html = buildPrintHtml(options);
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 50);
}
